/* eslint-disable no-console */
import { CertificationStatusChipOrIcon, EmbeddableVirtualConnectionTableDataQualityWarning } from '@tableau/data-catalog-client-components';
import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';

import { SiteUrlsProps } from '~src/app/selectors/SiteUrlsSelectors';
import { TagsMetadata } from '~src/app/spaces/Header/TagsMetadata';
import { CatalogApiRequests } from '~src/catalog/CatalogApiRequests';
import { catalogApolloClient } from '~src/catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '~src/catalog/CatalogFeatureFlagsAdapter';
import { CERTIFIED_INDICATOR_TYPE, dataQualityIndicatorInfosToProps } from '~src/catalog/components/dataQuality/DataQualityType';
import { dataQualityIndicatorInfoToDataQualityIndicator } from '~src/catalog/models/IDataQualityIndicatorInfo';
import { selectCatalogingOnSiteEnabled } from '~src/catalog/selectors/CatalogSelectors';
import { DataCatalogUIActionsService } from '~src/catalog/services/DataCatalogUIActionsService';
import { sanitize } from '~src/commonInfra/utils/Sanitize';
import {
  EditPublishedConnectionEntityAction,
} from '~src/publishedConnection/entityActions/EditPublishedConnectionEntityAction';
import { isPublishedConnectionTableInfo, PublishedConnectionTables } from '~src/publishedConnectionTable/resources/PublishedConnectionTables';
import { IPublishedConnectionTableActions } from '~src/publishedConnectionTable/serverApi/types/IPublishedConnectionTableActions';
import { ItemDescription } from '~src/spacesPlatform/components/header/HeaderItemDescription';
import {
  ExtractStatusMetadata,
  ModifiedDateMetadata,
  OwnerMetadata,
  PublishedConnectionPolicyMetaData,
} from '~src/spacesPlatform/components/header/HeaderMetadataRow';
import { ActionsMenuIcon, FavoriteToggleWithCountsIcon } from '~src/spacesPlatform/components/header/HeaderNameRowIcons';
import { ConnectedSpaceHeader, SpaceHeaderTestIds } from '~src/spacesPlatform/components/header/SpaceHeader';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ISpaceItemDetailInfo } from '~src/ts/models/ISpaceItemDetailInfo';
export interface PublishedConnectionTableSpaceHeaderProps extends SiteUrlsProps {
  readonly itemInfo: ISpaceItemDetailInfo<'virtualConnectionTable'>;
  readonly availableActions: IPublishedConnectionTableActions;
  readonly openDetail: () => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
}

export function PublishedConnectionTableSpaceHeader(props: PublishedConnectionTableSpaceHeaderProps): JSX.Element {
  const tagUrl = React.useCallback((tag: string) => props.urls.publishedConnectionTablesWithTagUrl(tag), [props.urls]);

  return (
    <ConnectedSpaceHeader
      item={props.itemInfo}
      availableActions={{ ...props.availableActions }}
      nameRowIcons={
        <React.Fragment>
          {FavoriteToggleWithCountsIcon({
            item: props.itemInfo,
            addToFavorites: props.addToFavorites,
            removeFromFavorites: props.removeFromFavorites,
          })}
          {ActionsMenuIcon(props.itemInfo)}
        </React.Fragment>
      }
      metadataRow={
        <React.Fragment>
          {OwnerMetadata(props.itemInfo.owner, props.urls)}
          {ModifiedDateMetadata(props.itemInfo.updatedAt)}
          {ExtractStatusMetadata(props.itemInfo.hasExtracts)}
          {isPublishedConnectionTableInfo(props.itemInfo) && PublishedConnectionPolicyMetaData(props.itemInfo)}
          {CertificationMetadata(props.itemInfo, props.urls)}
          {selectCatalogingOnSiteEnabled &&
            <DataQualityMetadata itemInfo={props.itemInfo} urls={props.urls} />}
        </React.Fragment>
      }
      spotlightActions={[EditPublishedConnectionEntityAction]}
      descriptionElement={ItemDescription(props.itemInfo, props.openDetail, sanitize)}
      bottomElement={TagsMetadata({ itemInfo: props.itemInfo, tagUrl })}
    />
  );
}

function DataQualityMetadata(props: {
  itemInfo: ISpaceItemDetailInfo<'virtualConnectionTable'>;
  urls: SiteUrls;
}): JSX.Element | null {
  const anchoredWarnings = props.itemInfo.dataQualityIndicators.filter(t => t.type !== CERTIFIED_INDICATOR_TYPE);
  const dataQualityWarningsOnThisItem = dataQualityIndicatorInfosToProps(
    {
      assetUrl: props.urls.publishedConnectionTableUrl(props.itemInfo.id),
      assetName: props.itemInfo.name,
      assetType: 'VirtualConnectionTable',
      onContentItem: true
    },
    props.itemInfo.dataQualityIndicators
  );

  return (
    <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
      <EmbeddableVirtualConnectionTableDataQualityWarning
        client={catalogApolloClient}
        vizportalUrlId={props.itemInfo.id}
        ownContentWarnings={dataQualityWarningsOnThisItem}
        siteUrls={props.urls}
        apiRequests={CatalogApiRequests}
        uiActions={DataCatalogUIActionsService.instance()}
        featureFlags={catalogFeatureFlagsAdapter()}
        asset={{
          assetName: props.itemInfo.name,
          assetType: 'VirtualConnectionTable',
          assetUrl: urls => urls.publishedConnectionTableUrl(props.itemInfo.id)
        }}
        anchoredWarnings={anchoredWarnings.map(i => dataQualityIndicatorInfoToDataQualityIndicator(i))}
      />
    </span>
  );
}

function CertificationMetadata(
  itemInfo: ISpaceItemDetailInfo<'virtualConnectionTable'>,
  urls: SpacesSiteUrls
): JSX.Element {
  const isCertified = PublishedConnectionTables.hasCertification(itemInfo);
  const indicators = itemInfo.dataQualityIndicators.filter(i => i.type === CERTIFIED_INDICATOR_TYPE);
  const indicator = indicators[0];

  return isCertified &&
    <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin} data-tb-test-id={SpaceHeaderTestIds.certifiedBy}>
      <CertificationStatusChipOrIcon
        subjectArea={'VirtualConnectionTable'}
        kind={'vizportal'}
        anchoredAsset={{
          assetName: itemInfo.name,
          assetType: 'VirtualConnectionTable',
          assetUrl: urls => urls.publishedConnectionTableUrl(itemInfo.id)
        }}
        anchoredCertifications={[dataQualityIndicatorInfoToDataQualityIndicator(indicator)]}
        iconOnly={false}
        urls={urls}
      />
    </span>;
}
